import React, { FC, useState, useCallback, useEffect } from "react";
import {
  useGetControllersListQuery,
  useUpdateControllerMutation,
  useAddControllerMutation,
  useGetControllersManagementFiltersQuery,
  useAddOrUpdateControllersManagementFilterMutation,
  useDeleteManagementFiltersMutation,
} from "features/api/usersPermissionsManagement/usersPermissionsManagementApiSlice";
import { PencilSquare, Plus, Pen } from "react-bootstrap-icons";
import BSButton from "react-bootstrap/Button";
import { Stack } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  ERROR_MESSAGE,
  FilterType,
  UserRole,
} from "components/Reports/reportAssets/assets";
import { useTranslation } from "react-i18next";
import MultiFilterContainer from "components/MultiFilterContainer/MultiFilterContainer";
import useFilterOptions from "components/MultiSelect/useFilterOptions";
import RenderList from "../RenderList";
import { controllerColumns } from "./controllerUtils";
import { TabTypes } from "../../UsersPermissionsManagementTypes";
import {
  ControllersListQueryBody,
  controllerData,
  newControllerData,
  updateControllerData,
} from "./controllerTypes";
import AddOrUpdateModal from "./AddOrUpdateModal";
import AutographPicModal from "./AutographPicModal";
import { formatObjetToNeededType } from "../volunteers/volunteerUtils";

type errorsType = {
  data: { errorMessage: string; propertyName: string }[] | { Message: string };
};

const Controllers: FC = () => {
  const { t } = useTranslation();
  const {
    filtersOptions,
    filterOptionInit,
    setDataToFiltersOptions,
    runFilter,
    setRunFilter,
    setFiltersOptions,
    chosenFilter,
    setChosenFilter,
    checkForSavedData,
    isOpenSavedFilter,
    closeSavedFilterComponent,
    toggleOpenMultiSelect,
    toggleOpenSavedFilter,
    openMultiSelect,
    isOpenMultiSelect,
    setIsOpenMultiSelect,
    setIsOpenSavedFilter,
  } = useFilterOptions(FilterType.controllersType);
  const [controllersListQueryBody, setControllersListQueryBody] =
    useState<ControllersListQueryBody>({});

  const submitControllersFilter = (filtersOptionsProps) => {
    if ("options" in filtersOptionsProps) {
      const formatedObject: ControllersListQueryBody =
        formatObjetToNeededType(filtersOptionsProps);
      setControllersListQueryBody(formatedObject);
    } else {
      setControllersListQueryBody(filtersOptionsProps);
    }
  };

  const { data: controllers } = useGetControllersListQuery({
    body: controllersListQueryBody,
  });

  const [updateController] = useUpdateControllerMutation();
  const [addController] = useAddControllerMutation();

  const [showAddOrUpdateModal, setShowAddOrUpdateModal] =
    useState<controllerData | null>(null);

  const [showAutographPicModal, setShowAutographPicModal] =
    useState<controllerData | null>(null);

  const [isModalForUpdate, setIsModalForUpdate] = useState<boolean>(false);

  const [registrationDate, setRegistrationDate] = useState();
  const [lastReportUpdate, setLastReportUpdate] = useState();
  // TODO:move to useFilterOPtions- setDatesStates
  const setDatesStates = (savedFilterDataForDates) => {
    if (savedFilterDataForDates.registrationDate) {
      setRegistrationDate((prevReportDate: any) => {
        const savedReportDate = checkForSavedData(
          "registrationDate",
          savedFilterDataForDates
        );
        return savedReportDate || prevReportDate;
      });
    }
    if (savedFilterDataForDates.lastReportUpdate) {
      setLastReportUpdate((prevUpdateDate: any) => {
        const savedUpdateDate = checkForSavedData(
          "lastReportUpdate",
          savedFilterDataForDates
        );

        return savedUpdateDate || prevUpdateDate;
      });
    }
  };

  const setDatatoFilter = useCallback(() => {
    setDataToFiltersOptions(chosenFilter);
  }, [chosenFilter, setDataToFiltersOptions]);
  useEffect(() => {
    if (Object.keys(chosenFilter).length) {
      setDatatoFilter();
      setDatesStates(chosenFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenFilter]);
  const [addOrUpdateFilter, { isLoading: isSaving }] =
    useAddOrUpdateControllersManagementFilterMutation();
  const {
    data: savedFilters,
    isLoading: isLoadingSavedFilters,
    isError: isErrorSavedFilters,
  } = useGetControllersManagementFiltersQuery();

  const [deleteFilter, { isLoading: isDeleting, isErrorDelete, onSuccess }] =
    useDeleteManagementFiltersMutation();
  const params = {
    multiSelect: {
      filterType: FilterType.controllersType,
      registrationDate,
      setRegistrationDate,
      lastReportUpdate,
      setLastReportUpdate,
      filtersOptions,
      setFiltersOptions,
      filterOptionInit,
      runFilter,
      setRunFilter,
      setChosenFilter,
      chosenFilter,
      dbRequest: {
        addOrUpdateFilter,
        isSaving,
      },
    },
    SavedFilters: {
      filterType: FilterType.controllersType,
      setChosenFilter,
      chosenFilter,
      openTheMultiCollapseFormOutSide: openMultiSelect,
      closeSavedFilterComponent,
      runFilter,
      setRunFilter,
      setRegistrationDate,
      setLastReportUpdate,
      filterOptionInit,
      dbRequest: {
        savedFilters,
        isLoading: isLoadingSavedFilters,
        isError: isErrorSavedFilters,
      },
      deleteDbRequest: {
        deleteFilter,
        isDeleting,
      },
    },
  };

  const displayErrors = (err: errorsType) => {
    if (Array.isArray(err?.data)) {
      err?.data.map((error) =>
        toast.error(
          t("{{errorMessage}}, שדה: {{propertyName}}", {
            errorMessage: error.errorMessage,
            propertyName: error.propertyName,
          }),
          {
            style: { direction: "rtl" },
          }
        )
      );
    } else if (err?.data) {
      toast.error(err?.data.Message, {
        style: { direction: "rtl" },
      });
    } else {
      console.error("Error:", err);
      toast.error(ERROR_MESSAGE);
    }
  };

  const sendUpdateController = async (data: controllerData) => {
    const payload: updateControllerData = {
      controllerId: data?.id,
      fullName: data?.fullName,
      email: data?.email,
      phoneNumber: data?.phoneNumber,
      password: data?.password ?? null,
      rolesCodeList: data?.rolesCodeList,
      tz: data?.tz,
      specialtiesCodeList: data?.specialtiesCodeList,
    };

    try {
      await updateController(payload).unwrap();
      setShowAddOrUpdateModal(null);
    } catch (err) {
      displayErrors(err);
    }
  };

  const sendNewController = async (data: controllerData) => {
    const payload: newControllerData = {
      fullName: data?.fullName,
      email: data?.email,
      phoneNumber: data?.phoneNumber,
      password: data?.password,
      rolesCodeList: data?.rolesCodeList,
      specialtiesCodeList: data?.specialtiesCodeList,
      tz: data?.tz,
    };

    try {
      await addController(payload).unwrap();
      setShowAddOrUpdateModal(null);
    } catch (err) {
      displayErrors(err);
    }
  };

  const handleUpdate = async (data: controllerData) => {
    isModalForUpdate ? sendUpdateController(data) : sendNewController(data);
  };

  const handleAddControllerClick = () => {
    const initialController: controllerData = {
      fullName: null,
      phoneNumber: null,
      email: null,
      registerDate: new Date().toDateString(),
      lastReportUpdate: new Date().toDateString(),
      specialtiesCodeList: [],
      disable: false,
      rolesCodeList: [],
      password: null,
      tz: "",
    };
    setShowAddOrUpdateModal(initialController);
    setIsModalForUpdate(false);
  };

  const editAction = {
    title: "",
    dataIndex: 11,
    flex: 1,
    renderCell: (controller: controllerData) => {
      return (
        <Stack direction="horizontal" gap={0}>
          <div
            className={`button-wrapper ${
              controller?.disable ? "disabled" : ""
            }`}
          >
            <BSButton key={controller?.id} variant="outline">
              <PencilSquare
                size={20}
                color="primary"
                onClick={() => {
                  setShowAddOrUpdateModal(controller);
                  setIsModalForUpdate(true);
                }}
              />
            </BSButton>
          </div>
        </Stack>
      );
    },
    maxWidth: 100,
  };

  const editAutographPic = {
    title: t("autographPic"),
    dataIndex: 12,
    flex: 1,
    renderCell: (controller: controllerData) => {
      return (
        <Stack direction="horizontal" gap={0}>
          <div
            className={`button-wrapper ${
              controller?.disable ? "disabled" : ""
            }`}
          >
            <BSButton key={controller?.id} variant="outline">
              <Pen
                size={20}
                color="primary"
                onClick={() => {
                  setShowAutographPicModal(controller);
                }}
              />
            </BSButton>
          </div>
        </Stack>
      );
    },
    maxWidth: 100,
  };

  return (
    <div className="tab-list-container">
      <div className="tab-list-header">
        <MultiFilterContainer
          isFollowUp={false}
          role={UserRole.CONTROLCENTER}
          isOpenMultiSelect={isOpenMultiSelect}
          isOpenSavedFilter={isOpenSavedFilter}
          setIsOpenMultiSelect={setIsOpenMultiSelect}
          setIsOpenSavedFilter={setIsOpenSavedFilter}
          params={params}
          toggleOpenMultiSelect={toggleOpenMultiSelect}
          toggleOpenSavedFilter={toggleOpenSavedFilter}
          filterType={FilterType.controllersType}
          submitFilter={submitControllersFilter}
        />
        <div className="header-add-button-container">
          <BSButton
            variant="primary"
            className="button-content"
            onClick={handleAddControllerClick}
          >
            <Plus color="#fff" />
            {t("add")}{" "}
          </BSButton>
        </div>
      </div>
      <RenderList
        data={controllers || []}
        columns={[...controllerColumns, editAutographPic, editAction]}
        type={"controlles" as TabTypes}
      />

      {!!showAddOrUpdateModal && (
        <AddOrUpdateModal
          isOpen={!!showAddOrUpdateModal}
          setIsOpen={setShowAddOrUpdateModal}
          submitForm={handleUpdate}
          initialValues={showAddOrUpdateModal}
          isForUpdate={isModalForUpdate}
        />
      )}

      {!!showAutographPicModal && (
        <AutographPicModal
          isOpen={!!showAutographPicModal}
          setIsOpen={setShowAutographPicModal}
          controller={showAutographPicModal}
        />
      )}
    </div>
  );
};

export default Controllers;
